import React, { useState } from 'react';
import { Card, CardContent, Typography, Button, Grid, Box, CircularProgress, Container, Stack, Avatar, Divider, Paper } from '@mui/material';
import { styled } from '@mui/system';
import { Post, apiURL } from '../../../utils/apiCalls';
import { toast } from 'react-toastify';
import RenderRazorpay from '../agents/RenderRazorpay';
import { useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';

const StyledCard = styled(Card)(({ theme, selected }) => ({
  border: selected ? '3px solid #1976d2' : '2px solid #e0e0e0',
  borderRadius: 16,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'visible',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
    border: '3px solid #1976d2',
  },
  ...(selected && {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 24px rgba(25, 118, 210, 0.3)',
  })
}));

const ProcurementSubscriptionPage = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [orderDetails, setOrderDetails] = useState({
    orderId: null,
    currency: null,
    amount: null,
  });
  const [loader, setLoader] = useState(null);
  const [displayRazorpay, setDisplayRazorpay] = useState(false);
  const [account, setAccount] = useState();
  const [logoutPage, setLogoutPage] = useState(false);

  React.useMemo(() => {
    setAccount(JSON.parse(sessionStorage.getItem('user_details')));
  }, [sessionStorage.getItem('user_details')]);

  React.useMemo(() => {
    setLogoutPage(sessionStorage.getItem('subscription'));
  }, [sessionStorage.getItem('subscription')]);
  
  // Get level_code from account
  const levelCode = account?.role?.level_code || 4;
  
  // Procurement subscription plans based on level_code
  const getPlanPrice = (levelCode) => {
    switch (parseInt(levelCode)) {
      case 1:
        return 9999;
      case 2:
        return 9999;
      case 3:
        return 7499;
      case 4:
        return 4999;
      default:
        return 4999;
    }
  };



  const plans = [
    { 
      id: parseInt(levelCode) || 4, 
      name: account?.role?.designation || 'Procurement Agent', 
      price: getPlanPrice(levelCode), 
      month: 12,
      description: 'Full access to procurement platform',
      features: [
        'Access to all procurement opportunities',
        'Real-time project updates',
        'Direct communication with clients',
        'Priority support',
        'Advanced analytics dashboard',
        'Document management system'
      ]
    }
  ];

  const handleCallBack = (response) => {
    console.log(response, "procurement subscription callback");

    if (response === 'SUCCESS') {
      setDisplayRazorpay(false);
      setLogoutPage(true);
      setLoader(null);
      toast.success('Subscription activated successfully!');
      // Redirect to procurement dashboard
      setTimeout(() => {
        navigate('/dashboard/procurement');
      }, 2000);
    }
  };

  const handleSelectPlan = async (plan) => {
    setSelectedPlan(plan);
    setLoader(plan.id);

    try {
      const response = await Post(apiURL + '/api/v1/payments/sub_payment_order', { 
        amount: plan.price, 
        month: plan.month,
        user_type: 'procurement'
      });

      if (response && response.data && response.data.status) {
        setOrderDetails({
          orderId: response.data.data.order_id,
          currency: response.data.data.currency,
          amount: response.data.data.amount,
        });
        setDisplayRazorpay(true);
      } else {
        toast.error(response?.data?.message || 'Failed to create order');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoader(null);
    }
  };

  const features = [
    'Access to all procurement opportunities',
    'Real-time project updates and notifications',
    'Direct communication with clients and partners',
    'Priority customer support',
    'Advanced analytics and reporting dashboard',
    'Secure document management system',
    '24/7 platform availability'
  ];

  if (logoutPage) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <Avatar sx={{ bgcolor: '#4caf50', width: 80, height: 80, mx: 'auto', mb: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h4" gutterBottom color="success.main" fontWeight="bold">
            Subscription Activated!
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Welcome to the Procurement Platform
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Your procurement agent subscription has been successfully activated. 
            You now have full access to all platform features.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/dashboard/procurement')}
            sx={{ px: 4, py: 1.5 }}
          >
            Go to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Avatar sx={{ 
          bgcolor: '#1976d2', 
          width: 80, 
          height: 80, 
          mx: 'auto', 
          mb: 3,
          boxShadow: '0 8px 16px rgba(25, 118, 210, 0.3)'
        }}>
          <BusinessIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold" color="text.primary">
          Procurement Agent Subscription
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          Unlock your potential as a procurement partner
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Join our network of successful procurement agents and gain access to exclusive opportunities, 
          advanced tools, and comprehensive support to grow your business.
        </Typography>
      </Box>

      {/* Features Section */}
      <Paper elevation={2} sx={{ p: 4, mb: 6, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 3 }}>
          What You Get
        </Typography>
        <Grid container spacing={2}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  {feature}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Subscription Plans */}
      <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" textAlign="center" sx={{ mb: 4 }}>
        Choose Your Plan
      </Typography>

      <Grid container spacing={4} justifyContent="center">
        {plans.map((plan) => (
          <Grid item xs={12} sm={8} md={6} lg={4} key={plan.id}>
            <StyledCard 
              selected={selectedPlan?.id === plan.id}
              onClick={() => setSelectedPlan(plan)}
            >
              {plan.id === 1 && (
                <Box sx={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: '#ff9800',
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  <StarIcon sx={{ fontSize: 16 }} />
                  Most Popular
                </Box>
              )}
              
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold" color="primary">
                  {plan.name}
                </Typography>
                
                <Box sx={{ my: 3 }}>
                  <Typography variant="h2" component="div" fontWeight="bold" color="text.primary">
                    ₹{plan.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    One-time payment
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {plan.description}
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mb: 4 }}>
                  {plan.features.map((feature, index) => (
                    <Stack key={index} direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        {feature}
                      </Typography>
                    </Stack>
                  ))}
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={() => handleSelectPlan(plan)}
                  disabled={loader === plan.id}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                    }
                  }}
                >
                  {loader === plan.id ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    `Subscribe for ₹${plan.price}`
                  )}
                </Button>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      {/* Footer Info */}
      <Box sx={{ textAlign: 'center', mt: 6, p: 4, bgcolor: 'grey.50', borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Why Choose Our Platform?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Join thousands of successful procurement agents who have grown their business with our platform. 
          Get access to verified opportunities, secure payments, and comprehensive support.
        </Typography>
      </Box>

      {displayRazorpay && (
        <RenderRazorpay
        amount={orderDetails.amount}
        currency={orderDetails.currency}
        orderId={orderDetails.orderId}
        keyId={'rzp_live_T0Fj5jdM5C2Q6F'}
        keySecret={''}
        email={account.email}
        phone={account.phone}
        page={'subcription'}
          handleCallBack={handleCallBack}
        />
      )}
    </Container>
  );
};

export default ProcurementSubscriptionPage;
